import { useEffect, useState } from "react";
import { api, inr } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "../../components/admin/ImageUploader";

const EMPTY = { name:"", slug:"", description:"", category_slug:"coffee-beans",
  images:[], variants:[{sku:"", label:"250g", price:0, cost_price:0, stock:0, low_stock_threshold:10}],
  ingredients:"", is_active:true, show_on_storefront:true, is_featured:false, is_bestseller:false, is_new:false, rating:4.5, review_count:0 };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then(r=>setProducts(r.data));
  useEffect(() => { load(); api.get("/admin/categories").then(r=>setCats(r.data)); }, []);

  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const updVar = (i,k,v) => setForm(f=>{ const vs=[...f.variants]; vs[i]={...vs[i],[k]:v}; return {...f,variants:vs}; });
  const delVar = (i) => setForm(f => {
    if ((f.variants || []).length <= 1) { toast.error("At least one variant is required"); return f; }
    const vs = f.variants.filter((_, idx) => idx !== i);
    return { ...f, variants: vs };
  });

  const save = async () => {
    try {
      const imgs = Array.isArray(form.images)
        ? form.images
        : String(form.images || "").split(",").map(s => s.trim()).filter(Boolean);
      const payload = {...form, images: imgs};
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const edit = (p) => {
    setEditing(p.product_id);
    setForm({...p, images: p.images || []});
    setOpen(true);
  };
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.product_id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-3xl font-outfit font-bold">Products</h1><div className="text-sm text-stone-500">{products.length} items</div></div>
        <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if (!v){setEditing(null); setForm(EMPTY);}}}>
          <DialogTrigger asChild><button data-testid="new-product-btn" className="bg-[#3B2F2F] text-white rounded-md px-4 py-2 flex items-center gap-2"><Plus className="w-4 h-4"/>New product</button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>{editing?"Edit":"New"} product</DialogTitle><DialogDescription>Product details, images, variants and merchandising flags.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <Input placeholder="Name" value={form.name} onChange={e=>upd("name",e.target.value)} data-testid="prod-name"/>
              <Input placeholder="Slug (optional)" value={form.slug} onChange={e=>upd("slug",e.target.value)}/>
              <Textarea placeholder="Description" value={form.description} onChange={e=>upd("description",e.target.value)}/>
              <Select value={form.category_slug} onValueChange={v=>upd("category_slug",v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{cats.map(c=><SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Image URLs (comma-separated)" value={Array.isArray(form.images) ? form.images.join(", ") : form.images} onChange={e=>upd("images",e.target.value)}/>
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Or upload images</div>
                <ImageUploader
                  value={Array.isArray(form.images) ? form.images : String(form.images||"").split(",").map(s=>s.trim()).filter(Boolean)}
                  onChange={(urls) => upd("images", urls)}
                  kind="products"
                />
              </div>
              <Input placeholder="Ingredients" value={form.ingredients} onChange={e=>upd("ingredients",e.target.value)}/>
              <div className="border-t pt-4">
                <div className="font-medium mb-2">Variants</div>
                {form.variants.map((v,i)=>(
                  <div key={i} className="mb-2" data-testid={`variant-row-${i}`}>
                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                      <Input placeholder="SKU" value={v.sku} onChange={e=>updVar(i,"sku",e.target.value)}/>
                      <Input placeholder="Label" value={v.label} onChange={e=>updVar(i,"label",e.target.value)}/>
                      <Input type="number" placeholder="Price" value={v.price} onChange={e=>updVar(i,"price",Number(e.target.value))}/>
                      <Input type="number" placeholder="Cost" value={v.cost_price} onChange={e=>updVar(i,"cost_price",Number(e.target.value))}/>
                      <Input type="number" placeholder="Stock" value={v.stock} onChange={e=>updVar(i,"stock",Number(e.target.value))}/>
                      <button type="button" onClick={()=>delVar(i)} data-testid={`delete-variant-${i}`}
                              title="Remove variant"
                              className="text-stone-400 hover:text-red-600 p-1">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 mt-1 pl-1">
                      <Input type="number" placeholder="Wholesale price (B2B)" value={v.wholesale_price ?? ""} onChange={e=>updVar(i,"wholesale_price", e.target.value === "" ? null : Number(e.target.value))} data-testid={`variant-wprice-${i}`} className="text-xs"/>
                      <Input type="number" placeholder="Min B2B qty" value={v.wholesale_min_qty ?? ""} onChange={e=>updVar(i,"wholesale_min_qty", e.target.value === "" ? null : Number(e.target.value))} data-testid={`variant-wmin-${i}`} className="text-xs"/>
                      <span className="text-[10px] text-stone-400 self-center pr-2">Blank = not sold wholesale</span>
                    </div>
                  </div>
                ))}
                <button onClick={()=>setForm(f=>({...f, variants:[...f.variants, {sku:"", label:"", price:0, cost_price:0, stock:0, low_stock_threshold:10}]}))} className="text-xs text-[#8B7355]">+ Add variant</button>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={e=>upd("is_active",e.target.checked)}/>Active</label>
                <label className="flex items-center gap-2" data-testid="prod-show-storefront"><input type="checkbox" checked={form.show_on_storefront!==false} onChange={e=>upd("show_on_storefront",e.target.checked)}/>Show on storefront</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={e=>upd("is_featured",e.target.checked)}/>Featured</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_bestseller} onChange={e=>upd("is_bestseller",e.target.checked)}/>Best seller</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_new} onChange={e=>upd("is_new",e.target.checked)}/>New</label>
              </div>
              <button onClick={save} data-testid="save-product-btn" className="w-full bg-[#3B2F2F] text-white rounded-md py-2">{editing?"Update":"Create"}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Product</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead>
            <TableHead>Stock</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {products.map(p => {
              const totalStock = p.variants?.reduce((s,v)=>s+(v.stock||0),0) || 0;
              return (
                <TableRow key={p.product_id} data-testid={`prod-row-${p.slug}`}>
                  <TableCell className="flex items-center gap-3">
                    <img src={p.images?.[0]} alt="" className="w-10 h-10 rounded object-cover"/>
                    <span className="font-medium">{p.name}</span>
                  </TableCell>
                  <TableCell><span className="text-xs">{p.category_slug}</span></TableCell>
                  <TableCell>{inr(p.min_price)}+</TableCell>
                  <TableCell>{totalStock}</TableCell>
                  <TableCell><Badge variant={p.is_active?"default":"secondary"}>{p.is_active?"Active":"Hidden"}</Badge></TableCell>
                  <TableCell><div className="flex gap-2">
                    <button onClick={()=>edit(p)} className="text-stone-500 hover:text-[#3B2F2F]"><Edit className="w-4 h-4"/></button>
                    <button onClick={()=>del(p)} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                  </div></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
